import { useDispatch, useSelector } from "react-redux";
import styled, { css } from "styled-components";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";

import Button from "@/components/Button";
import { Input, Label } from "@/components/Forms";
import Text from "@/components/Text";
import { Dispatch, RootState } from "@/utils/store";
import { api } from "@/utils/api";
import { handleFormErrors } from "@/utils/helpers";
import useAlerts from "@/hooks/useAlerts";
import { NewDropdown } from "@/components/Forms/Dropdown";
import ValidationMessage from "@/components/Forms/ValidationMessage";
import { FacebookPage } from "@/types/resources";
import { selectTeamTestingProperties } from "@/selectors";
import CurrencyField from "@/components/Forms/CurrencyField";
import { TestingCurrencies } from "@/utils/enums";
import { customFacebookAudiences } from "@/utils/data";
import { Dot } from "@/pages/OptimisationProject/components/OptimisationStatusBadge";
import ContentSpinner from "@/components/ContentSpinner";

const Card = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  margin-bottom: 40px;
`;

const HeadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: solid 1px ${({ theme }) => theme.colors.grayscaleLight};
`;

const Content = styled.section`
  padding: 24px;
`;

const FormLayout = styled.div`
  margin-top: 32px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 24px;
`;

const Authorization = styled(Card)`
  padding: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Expiration = styled(Text)`
  display: flex;
  align-items: center;
  ${Dot} {
    margin-right: 5px;
  }
`;

const AuthorizationButton = styled(Button)<{
  $disabled: boolean;
}>`
  ${({ $disabled, theme }) =>
    $disabled &&
    css`
      pointer-events: none;
      background: ${theme.colors.grayscaleLight};
      color: ${theme.colors.gray};
    `}
`;

export type FormDataT = {
  facebook_budget: string;
  final_value_facebook: string;
  facebook_page_id?: string;
  custom_facebook_audience: null | number;
};

const FacebookProperties = () => {
  const dispatch = useDispatch<Dispatch>();

  const { teamId } = useParams();

  const { testingSettings } = useSelector((state: RootState) =>
    selectTeamTestingProperties(state, teamId!)
  );

  const { data: facebookPages } = useQuery<FacebookPage[]>(
    ["facebook_testing_pages"],
    () => api.get<never, FacebookPage[]>("/v1/facebook-testing-pages"),
    {
      refetchOnMount: false,
    }
  );

  const { data: tokenInformation, isLoading } = useQuery<{
    hasToken: boolean;
    expiration?: string;
  }>(
    ["facebook_testing_token"],
    () =>
      api.get<
        never,
        {
          hasToken: boolean;
          expiration?: string;
        }
      >("/v1/facebook-testing-token"),
    {
      refetchOnMount: false,
    }
  );

  const alerts = useAlerts();

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    control,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<FormDataT>({
    defaultValues: {
      custom_facebook_audience:
        testingSettings?.custom_facebook_audience ?? null,
      facebook_budget: testingSettings?.facebook_budget?.toString() ?? "",
      facebook_page_id: testingSettings?.facebook_page_id?.toString(),
      final_value_facebook:
        testingSettings?.final_value_facebook?.toString() ?? "",
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        const properties = await api.post(
          `/v1/teams/${teamId}/testing-settings/properties`,
          {
            ...data,
            network: "facebook",
          }
        );

        dispatch.teams.updateTeamTestingProperties({
          properties,
          teamId: parseInt(teamId!),
        });

        alerts.success("Facebook testing properties saved");
      } catch (err) {
        handleFormErrors(err, setError, alerts);
      }
    })(e);
  };

  const daysUntilTokenExpires = Math.floor(
    tokenInformation?.expiration
      ? DateTime.fromISO(tokenInformation.expiration).diffNow("days").days
      : 0
  );

  if (isLoading) {
    return <ContentSpinner />;
  }

  return (
    <>
      <Authorization>
        <div>
          <Text $type="caption">Facebook ads authorization</Text>
          {!!daysUntilTokenExpires && (
            <Expiration $type="smallBold" $color="gray" as="div">
              {daysUntilTokenExpires <= 0 ? (
                <>
                  <Dot $color="red" />
                  Expired {-daysUntilTokenExpires} days before now.
                </>
              ) : (
                <>
                  <Dot
                    $color={daysUntilTokenExpires > 10 ? "green" : "yellow"}
                  />
                  {daysUntilTokenExpires} days left until expires.
                </>
              )}
            </Expiration>
          )}
        </div>
        <AuthorizationButton
          color="colorized"
          $disabled={daysUntilTokenExpires > 10}
          forwardedAs="a"
          href={`${
            process.env.REACT_APP_API_ENDPOINT
          }/facebook-testing?redirect=${encodeURIComponent(
            window.location.pathname
          )}`}
        >
          Authorize{!!tokenInformation?.hasToken && "d"}
        </AuthorizationButton>
      </Authorization>
      {!!tokenInformation?.hasToken && (
        <form onSubmit={onSubmit}>
          <Card>
            <HeadingRow>
              <div>
                <Text $type="caption" as="h3">
                  Facebook optimisation testing properties
                </Text>
              </div>
              <div>
                <Button
                  $type="colorized"
                  disabled={isSubmitting || !isDirty}
                  loading={isSubmitting}
                >
                  Save
                </Button>
              </div>
            </HeadingRow>
            <Content>
              <FormLayout>
                <div>
                  <Label htmlFor="facebookChannelSelect">Facebook page</Label>
                  <NewDropdown<string>
                    options={
                      facebookPages?.map((page) => ({
                        value: page.id,
                        label: page.name,
                      })) ?? []
                    }
                    control={control}
                    name="facebook_page_id"
                    id="facebookChannelSelect"
                  />
                  {!!errors.facebook_page_id && (
                    <ValidationMessage>
                      {errors.facebook_page_id.message}
                    </ValidationMessage>
                  )}
                </div>
                <div>
                  <Label htmlFor="facebookAudienceSelect">
                    Audience interests
                  </Label>
                  <NewDropdown<null | number>
                    options={customFacebookAudiences}
                    control={control}
                    name="custom_facebook_audience"
                    id="facebookAudienceSelect"
                  />
                  {!!errors.custom_facebook_audience && (
                    <ValidationMessage>
                      {errors.custom_facebook_audience.message}
                    </ValidationMessage>
                  )}
                </div>
                <div>
                  <Label htmlFor="budgetInput">Budget</Label>
                  <CurrencyField<FormDataT>
                    control={control}
                    name="facebook_budget"
                    inputId="budgetInput"
                    currency={TestingCurrencies.EUR}
                  />
                  {!!errors.facebook_budget && (
                    <ValidationMessage>
                      {errors.facebook_budget.message}
                    </ValidationMessage>
                  )}
                </div>
                <div>
                  <Label htmlFor="finalFacebookInput">Final Value</Label>
                  <Input
                    id="finalFacebookInput"
                    placeholder="Add value"
                    {...register("final_value_facebook")}
                    $hasError={!!errors.final_value_facebook}
                  />
                  {!!errors.final_value_facebook && (
                    <ValidationMessage>
                      {errors.final_value_facebook.message}
                    </ValidationMessage>
                  )}
                </div>
              </FormLayout>
            </Content>
          </Card>
        </form>
      )}
    </>
  );
};

export default FacebookProperties;
